import type { ConfigurationFormFieldOption } from 'src/services/configurationService';
import type { Workflow } from 'src/pages/ConfigurationPage/WorkflowsTab/workflowFormValues';
import type { ConfigurationFormFieldsObject } from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';

export interface ProjectLink {
  pf_page_name?: string;
  pf_page_description?: string;
  pf_page_url?: string;
}

// Sales role answers: two ideal-customer-profile steps and the calls to action.
export interface SalesRoleIcp {
  pf_icp_pain_point: string;
  pf_icp_solution: string;
  pf_icp_client_review: string;
}

export interface SalesRoleCta {
  pf_cta_value: string;
  pf_cta_condition: string;
}

// Persona values as stored in a prompt's input_data; buildPersonaSettingsPayload produces them.
// Languages are stored as option keys.
export type PersonaInputData = {
  pf_ai_name?: string;
  pf_company_name?: string;
  pf_company_description?: string;
  pf_pages?: ProjectLink[];
  pf_role?: string;
  pf_role_custom?: string;
  pf_cta?: SalesRoleCta[];
  pf_icp?: SalesRoleIcp[];
  pf_tone_of_voice_channel?: string;
  pf_tone_of_voice_style?: string;
  pf_tone_of_voice_style_custom?: string;
  pf_tone_of_voice_emoji?: boolean;
  pf_tone_of_voice_resp_len?: string;
  pf_default_language?: string;
  pf_excluded_languages?: string[];
  pf_words_blacklist?: string[];
  pf_custom_workflows?: Workflow[];
  pf_default_workflow?: Workflow;
};

// The persona form's values; languages are select options ({} when no language is chosen).
export interface PersonaFormValues {
  pf_ai_name: string;
  pf_company_name: string;
  pf_company_description: string;
  pf_pages: ProjectLink[];
  pf_role: string;
  pf_role_custom: string;
  pf_cta: SalesRoleCta[] | null;
  pf_icp: SalesRoleIcp[] | null;
  pf_tone_of_voice_channel: string;
  pf_tone_of_voice_style: string;
  pf_tone_of_voice_style_custom: string;
  pf_tone_of_voice_emoji: boolean;
  pf_tone_of_voice_resp_len: string;
  pf_default_language: Partial<ConfigurationFormFieldOption>;
  pf_excluded_languages: Partial<ConfigurationFormFieldOption>[];
  pf_words_blacklist: string[];
  pf_custom_workflows: Workflow[];
  pf_default_workflow: Workflow | Record<string, never>;
}

// The prompt builder step's values (onboarding and project creation).
export type PromptBuilderFormValues = Pick<
  PersonaFormValues,
  | 'pf_ai_name'
  | 'pf_company_name'
  | 'pf_company_description'
  | 'pf_role'
  | 'pf_role_custom'
  | 'pf_cta'
  | 'pf_icp'
>;

// The tone of voice step's values (onboarding and project creation).
export type ToneOfVoiceFormValues = Pick<
  PersonaFormValues,
  | 'pf_tone_of_voice_channel'
  | 'pf_tone_of_voice_style'
  | 'pf_tone_of_voice_resp_len'
  | 'pf_tone_of_voice_style_custom'
  | 'pf_tone_of_voice_emoji'
>;

const toLanguageOption = (key: string) => ({ key, value: key });

/**
 * Maps stored persona values to the form's values, falling back to the template's field
 * defaults and then to fixed defaults.
 */
export const getPersonaFormValues = (
  inputData: PersonaInputData | null | undefined,
  formFieldsObject: ConfigurationFormFieldsObject,
): PersonaFormValues => ({
  pf_ai_name: inputData?.pf_ai_name || '',
  pf_company_name: inputData?.pf_company_name || '',
  pf_company_description: inputData?.pf_company_description || '',
  pf_pages: inputData?.pf_pages || [],
  pf_role: inputData?.pf_role || formFieldsObject?.pf_role?.default_value?.value || 'support',
  pf_role_custom: inputData?.pf_role_custom || '',
  pf_cta: inputData?.pf_cta || null,
  pf_icp: inputData?.pf_icp || null,
  pf_tone_of_voice_channel:
    inputData?.pf_tone_of_voice_channel ||
    formFieldsObject?.pf_tone_of_voice_channel?.default_value?.value ||
    'chat',
  pf_tone_of_voice_style:
    inputData?.pf_tone_of_voice_style ||
    formFieldsObject?.pf_tone_of_voice_style?.default_value?.value ||
    'casual',
  pf_tone_of_voice_style_custom: inputData?.pf_tone_of_voice_style_custom || '',
  pf_tone_of_voice_emoji:
    typeof inputData?.pf_tone_of_voice_emoji === 'boolean'
      ? inputData.pf_tone_of_voice_emoji
      : formFieldsObject?.pf_tone_of_voice_emoji?.default_value?.value || false,
  pf_tone_of_voice_resp_len:
    inputData?.pf_tone_of_voice_resp_len ||
    formFieldsObject?.pf_tone_of_voice_resp_len?.default_value?.value ||
    'concise',
  pf_default_language: inputData?.pf_default_language
    ? toLanguageOption(inputData.pf_default_language)
    : formFieldsObject?.pf_default_language?.default_value || {},
  pf_excluded_languages: inputData?.pf_excluded_languages?.length
    ? inputData.pf_excluded_languages.map(toLanguageOption)
    : [],
  pf_words_blacklist: inputData?.pf_words_blacklist || [],
  pf_custom_workflows: inputData?.pf_custom_workflows || [],
  pf_default_workflow: inputData?.pf_default_workflow || {},
});
